import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, spRequest } from './authConfig';
import { getSiteId, ensureList } from './graphService';
import Header from './components/Header';
import ClarityTags from './components/ClarityTags';
import SubmitForm from './components/SubmitForm';
import WinsGallery from './components/WinsGallery';
import AdminPage from './components/AdminPage';
import './App.css';

function App() {
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [siteId, setSiteId] = useState(null);
  const [listId, setListId] = useState(null);
  const [initError, setInitError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      instance.loginRedirect(loginRequest).catch(console.error);
    }
  }, [isAuthenticated, inProgress, instance]);

  useEffect(() => {
    if (!isAuthenticated || inProgress !== InteractionStatus.None) return;

    async function init() {
      try {
        // Get a token scoped directly to SharePoint (bypasses Graph API restrictions)
        let spTokenRes;
        try {
          spTokenRes = await instance.acquireTokenSilent({
            ...spRequest,
            account: accounts[0],
          });
        } catch {
          // If silent fails, do interactive redirect for SP consent
          instance.loginRedirect({ ...spRequest, prompt: 'consent' });
          return;
        }

        const token = spTokenRes.accessToken;
        const sid = await getSiteId(token);
        setSiteId(sid);
        const lid = await ensureList(token, sid);
        setListId(lid);
      } catch (err) {
        setInitError(err.message);
      }
    }
    init();
  }, [isAuthenticated, inProgress, instance, accounts]);

  const handleSignOut = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  };

  if (!isAuthenticated) {
    return <div className="app-loading">Signing you in...</div>;
  }

  if (initError) {
    return (
      <div className="app-error-screen">
        <div className="app-error-card">
          <h2>Connection Error</h2>
          <p>Could not connect to SharePoint.</p>
          <p className="error-detail">{initError}</p>
          <button className="btn-primary" onClick={handleSignOut}>
            Sign Out &amp; Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <ClarityTags />
      <Header />
      <main className="app-main">
        {!siteId || !listId ? (
          <div className="app-loading">Connecting to SharePoint...</div>
        ) : (
          <Routes>
            <Route path="/" element={<Navigate to="/wins" replace />} />
            <Route path="/submit" element={<SubmitForm siteId={siteId} listId={listId} />} />
            <Route path="/wins" element={<WinsGallery siteId={siteId} listId={listId} />} />
            <Route path="/admin" element={<AdminPage siteId={siteId} listId={listId} />} />
          </Routes>
        )}
      </main>
    </BrowserRouter>
  );
}

export default App;
